import { supabase, fetchSupabaseState, getPublicCMSDirect, getPublicNoticesDirect, getPublicBlogsDirect, getPublicLegalPagesDirect, getPublicInstitutesDirect, getPublicPlansDirect, getPublicTestsDirect, getPublicQuestionBankDirect } from './supabase';
import { CMSContent, Question, Test, BlogPost, NoticeBoardItem, LegalPage, Institute, SubscriptionPlan, User } from '../types';

/**
 * DataService provides a centralized, strongly-typed interface for fetching and persisting
 * application data using the Supabase client and backend repositories.
 */
export const dataService = {
  // ==================== CMS OPERATIONS ====================
  async getCMS(): Promise<CMSContent> {
    try {
      const cms = await getPublicCMSDirect();
      return cms;
    } catch (err) {
      console.error('[DataService] Error loading CMS:', err);
      throw err;
    }
  },

  async updateCMS(updatedCms: Partial<CMSContent>): Promise<CMSContent> {
    try {
      const current = await this.getCMS();
      const newCms = { ...current, ...updatedCms };

      // Update in Supabase database_state
      const state = await fetchSupabaseState();
      const newState = { ...state, cms: newCms };

      const { error } = await supabase
        .from('database_state')
        .upsert({ id: 'main', data: newState });

      if (error) {
        console.warn('[DataService] Notice saving CMS to database_state:', error.message);
      } else {
        localStorage.setItem('cached_db_state', JSON.stringify(newState));
      }

      return newCms;
    } catch (err) {
      console.error('[DataService] Error updating CMS:', err);
      throw err;
    }
  },

  // ==================== TESTS & EXAMS OPERATIONS ====================
  async getTests(): Promise<Test[]> {
    try {
      const tests = await getPublicTestsDirect();
      return tests;
    } catch (err) {
      console.error('[DataService] Error loading tests:', err);
      return [];
    }
  },

  async getTestById(id: string): Promise<Test | null> {
    const tests = await this.getTests();
    return tests.find(t => t.id === id) || null;
  },

  async createOrUpdateTest(test: Test): Promise<Test> {
    const tests = await this.getTests();
    const existingIndex = tests.findIndex(t => t.id === test.id);
    let updatedTests: Test[];
    if (existingIndex >= 0) {
      updatedTests = [...tests];
      updatedTests[existingIndex] = test;
    } else {
      updatedTests = [...tests, test];
    }

    const state = await fetchSupabaseState();
    const newState = { ...state, tests: updatedTests };

    await supabase
      .from('database_state')
      .upsert({ id: 'main', data: newState });

    localStorage.setItem('cached_db_state', JSON.stringify(newState));
    return test;
  },

  async deleteTest(id: string): Promise<boolean> {
    const tests = await this.getTests();
    const updatedTests = tests.filter(t => t.id !== id);

    const state = await fetchSupabaseState();
    const newState = { ...state, tests: updatedTests };

    await supabase
      .from('database_state')
      .upsert({ id: 'main', data: newState });

    localStorage.setItem('cached_db_state', JSON.stringify(newState));
    return true;
  },

  // ==================== QUESTION BANK OPERATIONS ====================
  async getQuestions(): Promise<Question[]> {
    try {
      const questions = await getPublicQuestionBankDirect();
      return questions;
    } catch (err) {
      console.error('[DataService] Error loading question bank:', err);
      return [];
    }
  },

  async createOrUpdateQuestion(question: Question): Promise<Question> {
    const questions = await this.getQuestions();
    const idx = questions.findIndex(q => q.id === question.id);
    let updated: Question[];
    if (idx >= 0) {
      updated = [...questions];
      updated[idx] = question;
    } else {
      updated = [...questions, question];
    }

    const state = await fetchSupabaseState();
    const newState = { ...state, questionBank: updated };

    await supabase
      .from('database_state')
      .upsert({ id: 'main', data: newState });

    localStorage.setItem('cached_db_state', JSON.stringify(newState));
    return question;
  },

  async deleteQuestion(id: string): Promise<boolean> {
    const questions = await this.getQuestions();
    const updated = questions.filter(q => q.id !== id);

    const state = await fetchSupabaseState();
    const newState = { ...state, questionBank: updated };

    await supabase
      .from('database_state')
      .upsert({ id: 'main', data: newState });

    localStorage.setItem('cached_db_state', JSON.stringify(newState));
    return true;
  },

  // ==================== COMPETITIONS & SUBMISSIONS ====================
  async getCompetitionData(): Promise<{
    participants: any[];
    submissions: any[];
    votes: any[];
  }> {
    const state = await fetchSupabaseState();
    return {
      participants: state?.competitionParticipants || [],
      submissions: state?.competitionBlogs || [],
      votes: state?.competitionVotes || []
    };
  },

  async updateCompetitionBlogs(submissions: any[]): Promise<void> {
    const state = await fetchSupabaseState();
    const newState = { ...state, competitionBlogs: submissions };

    await supabase
      .from('database_state')
      .upsert({ id: 'main', data: newState });

    localStorage.setItem('cached_db_state', JSON.stringify(newState));
  },

  // ==================== INSTITUTES & PLANS ====================
  async getInstitutes(): Promise<Institute[]> {
    return getPublicInstitutesDirect();
  },

  async getPlans(): Promise<SubscriptionPlan[]> {
    return getPublicPlansDirect();
  },

  async getBlogs(): Promise<BlogPost[]> {
    return getPublicBlogsDirect();
  },

  async getNotices(): Promise<NoticeBoardItem[]> {
    return getPublicNoticesDirect();
  },

  async getLegalPages(): Promise<LegalPage[]> {
    return getPublicLegalPagesDirect();
  },

  // ==================== STUDENT TEST ATTEMPTS ====================
  async getStudentAttempts(studentId: string): Promise<any[]> {
    const state = await fetchSupabaseState();
    const allAttempts = state?.studentTests || [];
    // Enforce student ownership filter for RLS compliance
    return allAttempts.filter((att: any) => att.studentId === studentId);
  },

  async saveStudentAttempt(attempt: any): Promise<any> {
    const state = await fetchSupabaseState();
    const allAttempts = state?.studentTests || [];
    const idx = allAttempts.findIndex((a: any) => a.id === attempt.id);
    let updated: any[];
    if (idx >= 0) {
      updated = [...allAttempts];
      updated[idx] = attempt;
    } else {
      updated = [...allAttempts, attempt];
    }

    const newState = { ...state, studentTests: updated };

    await supabase
      .from('database_state')
      .upsert({ id: 'main', data: newState });

    localStorage.setItem('cached_db_state', JSON.stringify(newState));
    return attempt;
  }
};
